import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  scan,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Conversation, Message } from '@twilio/conversations';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';

import { trackBySID } from '@shared/utils';

import { MessengerService } from '../messenger.service';
import { ConversationsCollectionService, MessagesCollectionService, UsersCollectionService } from '../../../store/store-services';
import { ConversationStateModel, MessageStateModel } from '../../../store/store-models';
import { ROUTE_PARAM } from '../messenger.constants';
import { AppStateService } from '../../../store/app-state.service';
import { UserDataModel } from '@modules/auth';
import { LayoutManageService } from 'src/app/pages/_layout/services/layout-manage.service';

@Component({
  selector: 'app-chat-page',
  templateUrl: './chat-page.component.html',
  styleUrls: ['./chat-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  // @ViewChild('messageTextInput', {read: ElementRef, static: true}) messageTextInput: ElementRef<HTMLTextAreaElement>;
  @ViewChild('messagesContainer', {read: ElementRef, static: false}) messagesContainer: ElementRef<HTMLDivElement>;

  conversationUniqueId$: Observable<string> = this.route.params.pipe(
    map(({[ROUTE_PARAM.CONVERSATION_ID]: conversationUniqueId}) => conversationUniqueId),
    distinctUntilChanged(),
  );

  mainParticipantUser$: Observable<UserDataModel> = combineLatest([
    this.conversationUniqueId$,
    this.usersService.entityMap$,
  ]).pipe(
    map(([chatId, allUsersMap]) =>
      allUsersMap[this.getParticipantUserIdByConversationUniqueName(chatId)]),
    distinctUntilChanged(),
  );

  messages$: Observable<MessageStateModel[]> = combineLatest([
    this.conversationUniqueId$,
    this.messagesService.entities$
  ]).pipe(
    map(([chatId, messages]) =>
      messages.filter(message => message.conversationUniqueName === chatId)),
    shareReplay({bufferSize: 1, refCount: false}),
  );

  loadedMessagesCount$: Observable<number> = this.messages$.pipe(
    map(messages => messages.length),
    distinctUntilChanged(),
  );

  conversationState$: Observable<ConversationStateModel> = combineLatest([
    this.conversationUniqueId$,
    this.conversationsService.entityMap$
  ]).pipe(
    map(([chatId, conversationsRecord]) => conversationsRecord?.[chatId]),
    filter(it => Boolean(it)),
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: false}),
  );

  conversation$: Observable<Conversation> = this.conversationState$.pipe(
    map((conversationState) => this.conversationsService.entityMap.get(conversationState.uniqueName)),
    distinctUntilChanged(),
    shareReplay({bufferSize: 1, refCount: false}),
  );

  // TODO [1] use conversation$ instead of currentConversationPromise
  currentConversationPromise: Promise<Conversation | void>;

  messageFormGroup: FormGroup = new FormGroup({
    text: new FormControl({value: '', disabled: false}, {
      updateOn: 'change',
      validators: [
        Validators.required,
        Validators.pattern(/\S/) // not only spaces
      ],
      asyncValidators: []
    }),
  });

  trackByFn = trackBySID;

  intersectionObserver: IntersectionObserver;
  intersectionSubject: BehaviorSubject<number> = new BehaviorSubject<number>(-1);

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private messengerService: MessengerService,
    public appStateService: AppStateService,
    public conversationsService: ConversationsCollectionService,
    public messagesService: MessagesCollectionService,
    public usersService: UsersCollectionService,
    private changeDetectorRef: ChangeDetectorRef,
    private location: Location,
    public layoutManage: LayoutManageService,
  ) {
  }

  ngOnInit(): void {
    const userId = this.getParticipantUserId();
    // TODO [1] use conversation$ instead of currentConversationPromise
    this.currentConversationPromise = this.messengerService.getConversation(userId)
      .catch(err => console.warn('getConversation error:', err.message));

    this.mainParticipantUser$.pipe(takeUntil(this.destroy$)).subscribe((userData) => {
      this.layoutManage.setNavbarVisible(false);
      this.layoutManage.switchToSubheader({
        title: `Chat with ${userData?.userName || '...'}`,
        user: userData,
      });
    })

    this.conversation$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(conversation => {
      console.warn('[current conversation]', conversation);
      conversation.getMessages(1000).then(({items: messages}) => {
        this.messengerService.addMessages(messages);
      });
    });

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.filter(({isIntersecting}) => isIntersecting).forEach(({target}) => {
        // TODO find a better way to get message index from target without using DOM attributes
        const messageIndex = parseInt(target.getAttribute('data-message-index'), 10);
        // console.warn('[Intersection observer] Message item isIntersecting:', target, messageIndex);
        this.intersectionSubject.next(messageIndex);
      });
    }, {
      root: this.messagesContainer?.nativeElement,
      rootMargin: '0px',
      threshold: 0.6,
    });

    this.intersectionSubject.pipe(
      withLatestFrom(this.conversation$),
      filter(([nextIndex, conversation]) => nextIndex > (conversation.lastReadMessageIndex ?? -1)),
      scan(([prevIndex], [nextIndex, conversation]) =>
        [nextIndex > prevIndex ? nextIndex : prevIndex, conversation], [-1, null] as [number, Conversation]),
      distinctUntilChanged(([prevIndex], [nextIndex]) => nextIndex === prevIndex),
      debounceTime(300),
      tap(([nextMessageIndex]) => console.warn('[Intersection observer] nextMessageIndex:', nextMessageIndex)),
      // switchMap(([nextMessageIndex, conversation]: [number, Conversation]) =>
      //   conversation.advanceLastReadMessageIndex(nextMessageIndex)), 
      switchMap(([nextMessageIndex, conversation]: [number, Conversation]) =>
        conversation.updateLastReadMessageIndex(nextMessageIndex)),
      takeUntil(this.destroy$),
    ).subscribe((newUnreadCount: number) => {
      console.warn('newUnreadCount:', newUnreadCount);
    });
  }

  ngAfterViewInit(): void {
    // this.messageTextInput.nativeElement.focus({preventScroll: true});
  }

  ngOnDestroy(): void {
    this.layoutManage.resetState();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // @HostListener('keydown.shift.enter', ['$event'])
  @HostListener('keydown.enter', ['$event'])
  onEnterPress(e) {
    e.preventDefault();
    this.onFormSubmit();
  }

  onFormSubmit() {
    if (this.messageFormGroup.invalid) {
      return;
    }

    const messageText = this.messageFormGroup.value.text.trim();

    // TODO [1] use conversation$ instead of currentConversationPromise
    this.currentConversationPromise
      .then(conversation => conversation ?? this.messengerService.createConversation(this.getParticipantUserId()))
      .then(conversation => {
        // TODO [1] use conversation$ instead of currentConversationPromise
        this.currentConversationPromise = Promise.resolve(conversation);

        return this.sendMessage(conversation, messageText);
      })
      .then((messageIndex) => {
        console.log('Message send successfully', messageIndex);
        this.messageFormGroup.reset({text: ''});
        this.changeDetectorRef.detectChanges();
      })
      .catch(err => console.error('Message sending error:', err));
  }

  isMessageMine(msg: Message) {
    return this.messengerService.isMessageMine(msg);
  }

  getParticipantUserId() {
    return this.messengerService.parserOne2OneConversationUniqueName(
      this.route.snapshot.params[ROUTE_PARAM.CONVERSATION_ID]
    );
  }

  getParticipantUserIdByConversationUniqueName(conversationUniqueName: string): string {
    return this.messengerService.parserOne2OneConversationUniqueName(conversationUniqueName);
  }

  onWheel($event: WheelEvent) {
    // console.warn('[onWheel]', $event);
    $event.preventDefault();

    this.messagesContainer.nativeElement.scrollBy({
      top: ($event.deltaY * 0.6) * -1,
      behavior: 'auto'
    });
  }

  public back() {
    this.location.back();
  }

  // onScroll($event: Event) {
  //   console.warn('[onScroll]', $event);
  // }

  private sendMessage(conversation, messageText: string) {
    return conversation.prepareMessage().setBody(messageText)
      .build().send();
  }
}
